import { addDays, format, parseISO } from "date-fns";
import {
  Cuboid,
  Home,
  Pill,
  Shield,
  Stethoscope,
  Syringe,
  UserRound,
} from "lucide-react";

import type { IntakeDetails } from "@/api";
import { formatDate, SENSORS_DELIVERY_METHODS } from "@/utils";
import { buildAddressDetailsCardData } from "./buildAddressDetailsCardData";

export const getPatientDetailsCards = ({
  generalInformation,
  medicalInformation,
  insuranceInformation,
  addressInformation,
  productInformation,
  doctorInformation,
  dmeProvider,
}: IntakeDetails) => {
  const hasSameShippingAddress = addressInformation?.shippingSameAsBilling;

  const addressData = buildAddressDetailsCardData(
    addressInformation,
    hasSameShippingAddress,
  );

  const getLastSensorsFormattedDate = () => {
    if (!productInformation.lastSensorsReceivedAt) return "N/A";

    return format(
      parseISO(productInformation.lastSensorsReceivedAt),
      "MMMM yyyy",
    );
  };
  const getSensorsDeliveryMethodLabel = () => {
    if (
      productInformation.sensorsDeliveryMethod ===
      SENSORS_DELIVERY_METHODS.SENT_TO_HOME
    ) {
      return "Sent to home";
    } else if (
      productInformation.sensorsDeliveryMethod ===
      SENSORS_DELIVERY_METHODS.PICKUP_AT_PHARMACY
    ) {
      return "Pickup at Pharmacy";
    } else {
      return "N/A";
    }
  };

  const getCancelledWithLastSupplierLabel = () => {
    if (productInformation.cancelledWithLastSupplier === true) {
      return "Yes";
    } else if (productInformation.cancelledWithLastSupplier === false) {
      return "No";
    } else {
      return "N/A";
    }
  };

  const cardDetails = [
    {
      title: "General information",
      icon: <UserRound className="text-primary-600" size={16} />,
      data: [
        { label: "First name", value: generalInformation.firstName },
        { label: "Last name", value: generalInformation.lastName },
        {
          label: "Date of birth",
          value: formatDate(generalInformation.dateOfBirth),
        },
        { label: "Email", value: generalInformation.email },
        {
          label: "Phone",
          value: generalInformation.phoneNumber,
        },
      ],
      isCompleted: !!generalInformation,
    },
    {
      title: "Medical information",
      icon: <Pill className="text-primary-600" size={16} />,
      data: [
        {
          label: "Insulin",
          value: medicalInformation.usesInsulin ? "Yes" : "No",
        },
        {
          label: "Insulin name",
          value: medicalInformation?.insulinNames.join(", "),
        },
        {
          label: "Insulin Intake",
          value: medicalInformation.insulinPerDay
            ? `${medicalInformation.insulinPerDay} times a day`
            : undefined,
        },
        {
          label: "Insulin Intake",
          value: medicalInformation.usesInsulinPump
            ? "Insulin pump"
            : undefined,
        },
        {
          label: "Low blood sugar",
          value: !medicalInformation.usesInsulin ? "Yes" : undefined,
        },
      ],
      isCompleted: !!medicalInformation,
    },
    {
      title: "Insurance information",
      icon: <Shield className="text-primary-600" size={16} />,
      data: [
        {
          label: "Primary",
          value: `${insuranceInformation.primaryInsuranceName} ${insuranceInformation.primaryInsuranceId}`,
        },
        {
          label: "Secondary",
          value: insuranceInformation?.secondaryInsuranceName
            ? `${insuranceInformation.secondaryInsuranceName} ${insuranceInformation.secondaryInsuranceId}`
            : "N/A",
        },
        {
          label: "Policy name",
          value: insuranceInformation.policyName,
        },
      ],
      isCompleted: !!insuranceInformation,
    },
    {
      title: "Address information",
      icon: <Home className="text-primary-600" size={16} />,
      data: addressData,
      isCompleted: !!addressInformation,
    },
    {
      title: "Product information",
      icon: <Syringe className="text-primary-600" size={16} />,
      data: [
        {
          label: "CGM",
          value: productInformation?.productName ?? "Yes",
        },
        {
          label: "Using since",
          value: productInformation.startUsingCgmDate
            ? format(
                addDays(parseISO(productInformation.startUsingCgmDate), 1),
                "MMMM yyyy",
              )
            : undefined,
        },
        {
          label: "Sensors delivery method",
          value: getSensorsDeliveryMethodLabel(),
        },
        {
          label: "Cancelled with last supplier",
          value: getCancelledWithLastSupplierLabel(),
        },
        {
          label: "Last sensors supplier name",
          value: productInformation?.lastSensorsSupplierName ?? "N/A",
        },
        {
          label: "Last sensors received at",
          value: getLastSensorsFormattedDate(),
        },
        {
          label: "Last sensors received quantity",
          value:
            productInformation?.lastSensorsReceivedQty?.toString() ?? "N/A",
        },
      ],
      isCompleted: !!productInformation,
    },
    {
      title: "Doctor information",
      icon: <Stethoscope className="text-primary-600" size={16} />,
      data: [
        {
          label: "Doctor Name",
          value:
            `${doctorInformation.firstName ?? ""} ${
              doctorInformation.lastName ?? ""
            }`.trim() ?? "N/A",
        },

        {
          label: "NPI",
          value: doctorInformation.npi ?? "N/A",
        },
        {
          label: "Phone Number",
          value: doctorInformation.phone ?? "N/A",
        },
        {
          label: "Street Address",
          value: doctorInformation.streetAddress ?? "N/A",
        },

        {
          label: "City",
          value: doctorInformation.city ?? "N/A",
        },
        {
          label: "State",
          value: doctorInformation.state ?? "N/A",
        },
        {
          label: "ZIP Code",
          value: doctorInformation.zipCode ?? "N/A",
        },
      ],
      isCompleted: !!doctorInformation,
    },
  ];

  if (dmeProvider) {
    cardDetails.push({
      title: "DME Supplier",
      icon: <Cuboid className="text-primary-600" size={16} />,
      data: [
        {
          label: "Name",
          value: dmeProvider.name ?? "N/A",
        },
      ],
      isCompleted: !!dmeProvider,
    });
  } else {
    cardDetails.push({
      title: "DME Supplier",
      icon: <Cuboid className="text-primary-600" size={16} />,
      data: [
        {
          label: "Name",
          value: "Quest",
        },
      ],
      isCompleted: true,
    });
  }

  return cardDetails;
};
